import React, { useRef } from "react";
import Layout from "../components/Layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  CheckCircle2,
  Users,
  BookOpen,
  FileText,
  Award,
  Target,
  MessageSquare,
  Calendar,
} from "lucide-react";
import { Link } from "react-router-dom";
import PageMeta from "@/components/seo/PageMeta";
import { CourseSchema, FAQSchema } from "@/components/seo/schemas";
import { BOOKING_URL } from "@/config/constants";

const BlueprintPage: React.FC = () => {
  const pricingSectionRef = useRef<HTMLDivElement>(null);

  const outcomes = [
    "A clear board AI oversight model (roles, committees, cadence)",
    "A board‑level AI risk view (taxonomy, thresholds, escalation)",
    "A repeatable set of boardroom questions and decision prompts",
    "A management reporting template (what 'good' looks like)",
    "An incident escalation and materiality approach",
    "A board‑level dashboard concept and indicator set",
    "The BoardSight Oversight Toolkit (templates + checklists)",
    "A certificate of completion",
  ];

  const targetAudience = [
    "Directors / Non‑Executive Directors",
    "Audit, Risk, Compliance, and Tech Committee members",
    "Board Chairs and Committee Chairs",
    "Board‑facing leaders (GC, CRO, CISO, Compliance)",
  ];

  const whatsIncluded = [
    { icon: BookOpen, text: "12 bite‑size modules (board‑level, practical)" },
    { icon: FileText, text: "Downloadable templates + checklists" },
    { icon: Target, text: "Action exercises to translate learning into governance artifacts" },
    { icon: MessageSquare, text: "Optional office hours/Q&A (if cohort-based)" },
    { icon: Award, text: "Completion certificate" },
  ];

  const modules = [
    { number: 1, title: "Board accountability for AI", description: "Oversight vs management" },
    { number: 2, title: "AI risk landscape", description: "What boards should care about" },
    { number: 3, title: "Governance operating models", description: "Committee design + responsibilities" },
    { number: 4, title: "AI inventory & materiality", description: "What rises to board level" },
    { number: 5, title: "Risk taxonomy & thresholds", description: "Consistent governance" },
    { number: 6, title: "Controls, assurance & documentation", description: "Auditability" },
    { number: 7, title: "Third‑party/vendor AI oversight", description: "What to demand" },
    { number: 8, title: "Data, privacy & security intersections", description: "Board lens" },
    { number: 9, title: "Incident escalation & response", description: "Materiality + communications" },
    { number: 10, title: "Board reporting", description: "Dashboards + cadence" },
    { number: 11, title: "Decision rights & escalation pathways", description: "When board steps in" },
    { number: 12, title: "Adoption plan", description: "30/60/90 next steps" },
  ];

  const toolkitItems = [
    "Oversight responsibilities matrix (board/committee/management)",
    "Board AI reporting template (cadence + indicators)",
    "AI risk taxonomy starter + threshold prompts",
    "Incident escalation & materiality decision tree",
    "Vendor oversight checklist",
    "Board questions pack (by topic)",
    "30/60/90 governance roadmap",
  ];

  const pricingTiers = [
    {
      name: "Solo Director",
      price: "$1,799",
      description: "1 seat • full program + toolkit • certificate",
      cta: "Enroll as a Solo Director",
      featured: false,
    },
    {
      name: "Small Board",
      price: "$6,495",
      seats: "up to 5 seats",
      description: "Up to 5 seats • board rollout checklist",
      cta: "Enroll a Small Board",
      featured: true,
    },
    {
      name: "Medium Board",
      price: "$11,990",
      seats: "6–10 seats",
      description: "6–10 seats • rollout plan • optional follow‑up Q&A",
      cta: "Enroll a Medium Board",
      featured: false,
    },
  ];

  const faqs = [
    { question: "Self-paced or cohort?", answer: "Available as cohort-based and/or on-demand with optional office hours." },
    { question: "How much time does it take?", answer: "~12 hours across 30 days." },
    { question: "Is this legal advice?", answer: "No. Governance education and advisory support only." },
  ];

  return (
    <Layout>
      <PageMeta
        title="Board Director AI Oversight Intensive | 30‑Day Board Oversight Sprint"
        description="A 30‑day program for directors to build a defensible AI oversight framework: governance model, reporting expectations, incident escalation, templates, and certificate."
        canonicalUrl="/blueprint"
        keywords="AI oversight training, board director AI governance, AI governance framework, board AI oversight, director AI training, AI risk governance"
      />
      <CourseSchema price={1799} currency="USD" />
      <FAQSchema faqs={faqs} />

      <div className="container max-w-6xl py-12 md:py-20">
        {/* Hero Section */}
        <section className="text-center mb-16 md:mb-24">
          <Badge variant="secondary" className="mb-4">30‑Day Program</Badge>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-4">
            Director AI Oversight Intensive
          </h1>
          <p className="text-xl md:text-2xl text-muted-foreground font-medium mb-4">
            A 30‑Day Sprint to Build a Defensible AI Governance Oversight Framework
          </p>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto mb-8">
            A structured program for directors who need clarity, confidence, and board‑ready governance tools—without the technical noise.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
            <Button size="lg" asChild>
              <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                Book a Chair Call
              </a>
            </Button>
          </div>
          <p className="text-sm text-muted-foreground">12 short modules • practical exercises • templates • certificate</p>
        </section>

        {/* Outcomes */}
        <section className="mb-16 md:mb-24">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-10">By the end of the Intensive, you will have:</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {outcomes.map((outcome, index) => (
              <div key={index} className="flex items-start gap-3 p-4 rounded-lg bg-muted/50">
                <CheckCircle2 className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                <span className="text-foreground">{outcome}</span>
              </div>
            ))}
          </div>
        </section>

        <Separator className="my-16" />

        {/* Who It's For */}
        <section className="mb-16 md:mb-24">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-10">Who it's for</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {targetAudience.map((audience, index) => (
              <Card key={index} className="text-center">
                <CardContent className="pt-6">
                  <Users className="h-10 w-10 text-primary mx-auto mb-4" />
                  <p className="font-medium">{audience}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* What's Included */}
        <section className="mb-16 md:mb-24">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-10">What's included</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {whatsIncluded.map((item, index) => (
              <div key={index} className="flex items-center gap-4 p-5 rounded-lg border bg-card">
                <item.icon className="h-8 w-8 text-primary shrink-0" />
                <span className="font-medium">{item.text}</span>
              </div>
            ))}
          </div>
        </section>

        <Separator className="my-16" />

        {/* Modules */}
        <section className="mb-16 md:mb-24">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">12 Modules</h2>
          <p className="text-center text-muted-foreground mb-10 max-w-2xl mx-auto">
            Board‑level content designed for busy directors. Each module builds toward a complete oversight framework.
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {modules.map((module) => (
              <Card key={module.number} className="hover:shadow-md transition-shadow">
                <CardContent className="p-5">
                  <div className="flex items-start gap-4">
                    <Badge variant="outline" className="shrink-0">{module.number}</Badge>
                    <div>
                      <h3 className="font-semibold mb-1">{module.title}</h3>
                      <p className="text-sm text-muted-foreground">{module.description}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* Toolkit */}
        <section className="mb-16 md:mb-24 bg-muted/30 rounded-2xl p-8 md:p-12">
          <div className="flex items-center justify-center gap-3 mb-8">
            <FileText className="h-8 w-8 text-primary" />
            <h2 className="text-3xl md:text-4xl font-bold">The Oversight Toolkit</h2>
          </div>
          <p className="text-center text-muted-foreground mb-8 max-w-2xl mx-auto">
            Board‑ready templates and checklists included with every enrollment.
          </p>
          <div className="grid md:grid-cols-2 gap-4 max-w-4xl mx-auto">
            {toolkitItems.map((item, index) => (
              <div key={index} className="flex items-center gap-3 p-4 rounded-lg bg-background border">
                <CheckCircle2 className="h-5 w-5 text-primary shrink-0" />
                <span>{item}</span>
              </div>
            ))}
          </div>
        </section>

        <Separator className="my-16" />

        {/* Pricing */}
        <section id="pricing" ref={pricingSectionRef} className="mb-16 md:mb-24 scroll-mt-8">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">Pricing</h2>
          <p className="text-center text-muted-foreground mb-10">Choose the option that fits your board's needs.</p>
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            {pricingTiers.map((tier, index) => (
              <Card key={index} className={`relative ${tier.featured ? "border-primary shadow-lg" : ""}`}>
                {tier.featured && <Badge className="absolute -top-3 left-1/2 -translate-x-1/2">Most Popular</Badge>}
                <CardHeader className="text-center pb-4">
                  <CardTitle className="text-xl">{tier.name}</CardTitle>
                  {tier.seats && <p className="text-sm text-muted-foreground">{tier.seats}</p>}
                </CardHeader>
                <CardContent className="text-center">
                  <p className="text-4xl font-bold mb-4">{tier.price}</p>
                  <p className="text-muted-foreground mb-6">{tier.description}</p>
                  <Button className="w-full" variant={tier.featured ? "default" : "outline"} asChild>
                    <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      Book a Briefing
                    </a>
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>

          <Card className="bg-gradient-to-r from-primary/5 to-primary/10 border-primary/20">
            <CardContent className="p-8 text-center">
              <Badge variant="secondary" className="mb-4">Premium</Badge>
              <h3 className="text-2xl font-bold mb-2">Board Oversight Sprint</h3>
              <p className="text-muted-foreground mb-2">Advisory + Intensive Combined</p>
              <p className="text-3xl font-bold mb-4">$18k–$25k</p>
              <p className="text-muted-foreground mb-6">Up to 10 seats + live workshops + light customization</p>
              <Button size="lg" variant="outline" asChild>
                <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2">
                  <Calendar className="h-5 w-5" />
                  Book a Briefing
                </a>
              </Button>
            </CardContent>
          </Card>
        </section>

        <Separator className="my-16" />

        {/* FAQ */}
        <section className="mb-16 md:mb-24">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-10">Frequently Asked Questions</h2>
          <div className="max-w-3xl mx-auto">
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((faq, index) => (
                <AccordionItem key={index} value={`faq-${index}`}>
                  <AccordionTrigger className="text-left">{faq.question}</AccordionTrigger>
                  <AccordionContent>{faq.answer}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* Final CTA */}
        <section className="text-center py-12 px-6 rounded-2xl bg-muted/50">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Build the oversight muscle your board needs—without technical overload.
          </h2>
          <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
            Join directors who are building defensible AI governance frameworks.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" asChild>
              <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                Book a Chair Call
              </a>
            </Button>
          </div>
        </section>
      </div>
    </Layout>
  );
};

export default BlueprintPage;
